// Bulk WO Paste — /admin/wo-paste
// Paste Mario/John's forwarded text into a box, AI parses it into draft
// WOs, admin edits inline, one-click creates them all into the Pool.

import { useState } from "react";
import { toast } from "sonner";
import {
  ClipboardText, MagicWand, Trash, CheckCircle, Warning, Plus, X as XIcon,
} from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";

const CATEGORIES = [
  "Plumbing", "HVAC / Heat / AC", "Electrical", "Appliance",
  "Pest / Rodent", "Lock / Key / Door", "Leak / Water Damage",
  "Roof / Ceiling", "Other",
];
const PRIORITIES = ["normal", "high", "urgent"];
const PRIORITY_COLOR = {
  normal: "bg-slate-100 text-slate-700 border-slate-300",
  high: "bg-amber-100 text-amber-800 border-amber-400",
  urgent: "bg-rose-100 text-rose-800 border-rose-500",
};

const EXAMPLE = `Apartment 3A - requesting mouse control
Apartment 3A - toxic smell when oven is turned on
Apartment 3B - no hot water in shower
Apartment 12A - stack pipe cracked in basement`;

export default function AdminWOPaste() {
  const [text, setText] = useState("");
  const [defaultAddress, setDefaultAddress] = useState("");
  const [parsing, setParsing] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null); // { created: [], errors: [] }

  const parse = async () => {
    if (text.trim().length < 3) {
      toast.error("Paste at least a few words first");
      return;
    }
    setParsing(true);
    setResult(null);
    try {
      const r = await api.post("/wo-paste/parse", {
        text,
        default_property_address: defaultAddress,
      });
      const arr = r.data.drafts || [];
      setDrafts(arr.map(d => ({ ...d, _keep: true })));
      if (r.data.warning) toast.warning(r.data.warning);
      else if (!arr.length) toast.warning("No work orders detected in that text");
      else toast.success(`Parsed ${arr.length} draft${arr.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Parse failed");
    } finally { setParsing(false); }
  };

  const patchDraft = (i, partial) => {
    setDrafts(prev => prev.map((d, idx) => idx === i ? { ...d, ...partial } : d));
  };
  const dropDraft = (i) => {
    setDrafts(prev => prev.filter((_, idx) => idx !== i));
  };

  const createAll = async () => {
    const keep = drafts.filter(d => d._keep !== false);
    if (!keep.length) { toast.error("Nothing to create"); return; }
    // client-side sanity: every draft needs a property_address + title
    const bad = keep.find(d => !(d.property_address || "").trim() || !(d.title || "").trim());
    if (bad) { toast.error("Every draft needs a property address and a title"); return; }
    setCreating(true);
    try {
      const payload = keep.map(({ _keep, ...rest }) => rest);
      const r = await api.post("/wo-paste/create-batch", { drafts: payload });
      setResult(r.data);
      toast.success(`Created ${r.data.created_count} work order${r.data.created_count === 1 ? "" : "s"}`);
      if (!r.data.errors?.length) {
        // fully-successful — clear so admin can paste the next bundle
        setDrafts([]);
        setText("");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Create failed");
    } finally { setCreating(false); }
  };

  const keepCount = drafts.filter(d => d._keep !== false).length;

  return (
    <AdminShell title="Bulk WO Paste">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {/* Header */}
        <div className="rounded-lg overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-950 text-white p-6">
          <div className="text-[10px] font-black uppercase tracking-widest text-indigo-300 mb-1">
            Field Radar · AI Dispatcher
          </div>
          <h1 className="font-display font-black text-3xl leading-none tracking-tight mb-2">
            Text → Work Orders
          </h1>
          <p className="text-sm text-white/70 max-w-2xl">
            Paste any forwarded text — from Mario, John, tenants, whatever — Claude reads it, extracts each distinct issue, and drops draft WOs into the Pool. Edit before committing.
          </p>
        </div>

        {/* Input */}
        <section className="bg-white border-2 border-slate-200 rounded-lg p-4 space-y-3" data-testid="wo-paste-input">
          <div className="flex items-center gap-2">
            <ClipboardText size={16} className="text-slate-500"/>
            <div className="font-black uppercase text-[11px] tracking-widest text-slate-500">
              Paste the raw text
            </div>
          </div>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={EXAMPLE}
            rows={7}
            data-testid="wo-paste-textarea"
            className="w-full px-3 py-2 border-2 border-slate-300 font-mono text-sm resize-y focus:border-indigo-500 focus:outline-none"/>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 whitespace-nowrap">
              Building context (optional)
            </label>
            <input
              value={defaultAddress}
              onChange={e => setDefaultAddress(e.target.value)}
              placeholder="e.g. 5346 Chew Ave"
              data-testid="wo-paste-default-address"
              className="flex-1 h-9 px-2 border-2 border-slate-300 text-sm"/>
            <button
              onClick={parse}
              disabled={parsing || text.trim().length < 3}
              data-testid="wo-paste-parse-btn"
              className={`h-10 px-4 inline-flex items-center justify-center gap-1.5 font-black uppercase text-[11px] tracking-widest whitespace-nowrap ${
                parsing || text.trim().length < 3
                  ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700 text-white"
              }`}>
              <MagicWand size={12}/> {parsing ? "Parsing…" : "Parse with AI"}
            </button>
          </div>
        </section>

        {/* Drafts */}
        {drafts.length > 0 && (
          <section className="bg-white border-2 border-slate-200 rounded-lg p-4" data-testid="wo-paste-drafts">
            <div className="flex items-center gap-2 mb-3">
              <div className="font-black uppercase text-[11px] tracking-widest text-slate-500">
                Draft Work Orders · {keepCount} of {drafts.length}
              </div>
              <div className="ml-auto text-[10px] text-slate-400 italic">
                Edit anything below, then create
              </div>
            </div>

            <div className="space-y-3">
              {drafts.map((d, i) => (
                <div
                  key={i}
                  data-testid={`wo-paste-draft-${i}`}
                  className={`border-2 rounded p-3 ${
                    d._keep === false
                      ? "border-slate-200 bg-slate-50 opacity-50"
                      : "border-slate-300 bg-white"
                  }`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0 space-y-2">
                      {/* Row 1 — address + unit + priority + category */}
                      <div className="grid grid-cols-12 gap-2">
                        <input list="known-addresses" autoComplete="off"
                          value={d.property_address}
                          onChange={e => patchDraft(i, { property_address: e.target.value })}
                          placeholder="Address"
                          data-testid={`wo-paste-address-${i}`}
                          className="col-span-6 sm:col-span-5 h-9 px-2 border-2 border-slate-300 text-sm"/>
                        <input
                          value={d.unit}
                          onChange={e => patchDraft(i, { unit: e.target.value })}
                          placeholder="Unit"
                          data-testid={`wo-paste-unit-${i}`}
                          className="col-span-2 h-9 px-2 border-2 border-slate-300 text-sm text-center font-black"/>
                        <select
                          value={d.category}
                          onChange={e => patchDraft(i, { category: e.target.value })}
                          data-testid={`wo-paste-category-${i}`}
                          className="col-span-4 sm:col-span-3 h-9 px-1 border-2 border-slate-300 text-xs bg-white">
                          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <select
                          value={d.priority}
                          onChange={e => patchDraft(i, { priority: e.target.value })}
                          data-testid={`wo-paste-priority-${i}`}
                          className={`col-span-12 sm:col-span-2 h-9 px-1 border-2 text-xs font-black uppercase ${PRIORITY_COLOR[d.priority] || "bg-white border-slate-300"}`}>
                          {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                      {/* Row 2 — title */}
                      <input
                        value={d.title}
                        onChange={e => patchDraft(i, { title: e.target.value })}
                        placeholder="Title"
                        data-testid={`wo-paste-title-${i}`}
                        className="w-full h-9 px-2 border-2 border-slate-300 text-sm font-black"/>
                      {/* Row 3 — description */}
                      <textarea
                        value={d.description}
                        onChange={e => patchDraft(i, { description: e.target.value })}
                        placeholder="Description"
                        rows={2}
                        data-testid={`wo-paste-description-${i}`}
                        className="w-full px-2 py-1.5 border-2 border-slate-300 text-xs resize-y"/>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <button
                        onClick={() => patchDraft(i, { _keep: d._keep === false })}
                        title={d._keep === false ? "Include again" : "Skip this one"}
                        data-testid={`wo-paste-skip-${i}`}
                        className={`h-7 w-7 flex items-center justify-center border-2 rounded ${
                          d._keep === false
                            ? "border-slate-300 bg-white text-slate-400"
                            : "border-emerald-500 bg-emerald-50 text-emerald-700"
                        }`}>
                        <CheckCircle size={14} weight={d._keep === false ? "regular" : "fill"}/>
                      </button>
                      <button
                        onClick={() => dropDraft(i)}
                        title="Remove"
                        data-testid={`wo-paste-drop-${i}`}
                        className="h-7 w-7 flex items-center justify-center text-rose-500 hover:bg-rose-50 rounded">
                        <Trash size={13}/>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:items-center">
              <button
                onClick={() => { setDrafts([]); setResult(null); }}
                data-testid="wo-paste-clear"
                className="h-9 px-3 border-2 border-slate-300 hover:bg-slate-50 font-black uppercase text-[10px] tracking-widest">
                Clear drafts
              </button>
              <div className="flex-1"/>
              <button
                onClick={createAll}
                disabled={creating || !keepCount}
                data-testid="wo-paste-create-btn"
                className={`h-10 px-5 inline-flex items-center justify-center gap-1.5 font-black uppercase text-[11px] tracking-widest whitespace-nowrap ${
                  creating || !keepCount
                    ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                    : "bg-emerald-600 hover:bg-emerald-700 text-white"
                }`}>
                <Plus size={12}/> {creating ? "Creating…" : `Create ${keepCount} work order${keepCount === 1 ? "" : "s"}`}
              </button>
            </div>
          </section>
        )}

        {/* Result */}
        {result && (
          <section className="bg-emerald-50 border-2 border-emerald-400 rounded-lg p-4" data-testid="wo-paste-result">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle weight="fill" className="text-emerald-700" size={16}/>
              <div className="font-black uppercase text-[11px] tracking-widest text-emerald-900">
                {result.created_count} Created → Pool
              </div>
              <a
                href="/admin/jobs-board"
                data-testid="wo-paste-goto-board"
                className="ml-auto text-[10px] text-emerald-700 hover:text-emerald-900 font-black uppercase tracking-widest underline">
                Open Jobs Board →
              </a>
            </div>
            {result.created?.length > 0 && (
              <div className="space-y-1 mb-2">
                {result.created.map((c, i) => (
                  <div key={i} className="text-xs text-emerald-900 flex items-center gap-2">
                    <CheckCircle size={11} className="text-emerald-600 flex-shrink-0"/>
                    <span className="font-black">Unit {c.unit || "—"}</span>
                    <span className="truncate">· {c.title}</span>
                  </div>
                ))}
              </div>
            )}
            {result.errors?.length > 0 && (
              <div className="mt-2 pt-2 border-t border-emerald-300">
                <div className="text-[10px] font-black uppercase tracking-widest text-rose-700 mb-1 inline-flex items-center gap-1">
                  <Warning size={11}/> {result.errors.length} skipped
                </div>
                <div className="space-y-1">
                  {result.errors.map((e, i) => (
                    <div key={i} className="text-xs text-rose-700">Row {e.index + 1}: {e.error}</div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </AdminShell>
  );
}
